import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import { COUNTRIES, INCOTERMS } from './data/countries';
import { getEtiquetageRules } from './data/etiquetage';
import { t } from './data/i18n';
import { CONTENT } from './data/content';
import { LLM_PROVIDER, LLM_API_KEY } from './config';
import { getAccordPreferentiel, calculerDroits, detecterEconomies, classifyProduct, DEFAULT_TC } from './engine/tradeEngine';
import { getDocumentsByChapter } from './engine/documentsEngine';
import { generateChecklistPDF } from './engine/pdfGenerator';
import { generatePPIExcel } from './engine/excelGenerator';
import { generateEmailFournisseur } from './engine/emailGenerator';
import PPIBuilder from './components/PPIBuilder';
import ExportModule from './components/export/ExportModule';
import VerifyModule from './components/verify/VerifyModule';
import QuoteRequestForm from './components/marketplace/QuoteRequestForm';
import ForwarderRegistration from './components/marketplace/ForwarderRegistration';
import ForwarderDashboard from './components/marketplace/ForwarderDashboard';
import ForwarderWallet from './components/marketplace/ForwarderWallet';
import ForwardersPublicPage from './components/marketplace/ForwardersPublicPage';
import AdminPage from './pages/AdminPage';
import EmailGateModal from './components/freemium/EmailGateModal';
import BlurredOverlay from './components/freemium/BlurredOverlay';
import UserDashboard from './components/UserDashboard';
import LandingHome from './components/LandingHome';
import { getAnalysisCount, incrementAnalysis, canAnalyze, loadSettings, getSettings, loadUserCredits, consumeCredit } from './lib/freemium';
import { getSession, getUser, signOut, onAuthChange, trackAnalysis } from './lib/supabase';

const CODES = [{ value:'01', key:'act_01' },{ value:'07', key:'act_07' },{ value:'04', key:'act_04' },{ value:'NESDA', key:'act_nesda' }];
const DEVISES = ['USD','EUR','GBP','CNY','DZD'];
const FMT = (n) => n?.toLocaleString('fr-DZ') || '0';

// Scroll reveal hook
function useReveal() {
  const cb = useCallback((entries) => {
    entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
  }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(cb, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  });
}

function MainApp() {
  const [lang, setLang] = useState(() => localStorage.getItem('tidjarai_lang') || 'fr');
  const [page, setPage] = useState('home');
  const [form, setForm] = useState({ description:'', pays:'', codeActivite:'', prix:'', devise:'USD', incoterm:'CIF' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailLang, setEmailLang] = useState('fr');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tauxChange, setTauxChange] = useState('');
  const [showGate, setShowGate] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [blurred, setBlurred] = useState(false);
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const ref = useRef(null);
  useReveal();

  // Auth + settings
  useEffect(() => {
    // Check cached session immediately
    const cached = localStorage.getItem('tidjarai-auth');
    if (cached) try { const d = JSON.parse(cached); if (d?.user) { setUser(d.user); setSession(d); } } catch {}

    getSession().then(s => { setSession(s); if (s) getUser().then(u => { setUser(u); setAuthLoading(false); if (u?.email) loadUserCredits(u.email); }); else setAuthLoading(false); });
    const { data: { subscription } } = onAuthChange((s) => { setSession(s); if (s) getUser().then(setUser); else setUser(null); });
    loadSettings();
    return () => subscription.unsubscribe();
  }, []);

  const T = (k) => t(lang, k);
  const ar = lang === 'ar';
  const C = CONTENT[lang] || CONTENT.fr;
  const date = new Date().toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });

  useEffect(() => { try { setHistory(JSON.parse(localStorage.getItem('tidjarai_history') || localStorage.getItem('tradeflow_history') || '[]')); } catch {} }, []);
  const saveH = (e) => { const h = [e, ...history].slice(0, 20); setHistory(h); localStorage.setItem('tidjarai_history', JSON.stringify(h)); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.pays || !form.codeActivite) return;

    // Freemium check — BLOCK BEFORE calling DeepSeek to save tokens
    const allowed = canAnalyze(session);
    if (allowed === 'needsEmail') { setShowGate(true); return; }
    if (allowed === 'paywall') { setShowPaywall(true); return; }

    incrementAnalysis();
    setLoading(true); setCopied(false);
    try {
      const cls = await classifyProduct(form.description, LLM_PROVIDER, LLM_API_KEY, lang);
      if (!cls) { setResult({ error: true }); setLoading(false); return; }
      const acc = getAccordPreferentiel(form.pays, cls.code_sh, lang);
      let calc = null;
      if (form.prix && parseFloat(form.prix) > 0)
        calc = calculerDroits({ valeur: parseFloat(form.prix), devise: form.devise, incoterm: form.incoterm, taux_DD: cls.taux_DD, taux_TVA: cls.taux_TVA, daps_taux: cls.daps_taux, pays: form.pays, accord: acc, codeActivite: form.codeActivite, tauxChange: parseFloat(tauxChange) || undefined });
      const zone = acc.type?.includes('UE') || acc.type?.includes('الأوروبي') ? 'EU' : acc.type?.includes('GZALE') || acc.type?.includes('العربية') ? 'GZALE' : 'OTHER';
      const docs = getDocumentsByChapter(cls.code_sh, zone, form.codeActivite, lang);
      const econ = detecterEconomies(cls.code_sh, form.pays, calc);
      const emails = generateEmailFournisseur({ produit: form.description, pays: form.pays, hsCode: cls.code_sh, codeActivite: form.codeActivite });
      const etiq = getEtiquetageRules(cls.categorie, lang);
      setResult({ classification: cls, accord: acc, calcul: calc, documents: docs, economies: econ, emails, etiquetage: etiq });
      saveH({ date: new Date().toISOString(), description: form.description, pays: form.pays, codeActivite: form.codeActivite, code_sh: cls.code_sh, form: { ...form }, result: { classification: cls, accord: acc, calcul: calc, documents: docs, economies: econ, emails, etiquetage: etiq } });
      trackAnalysis('import', form.description, cls.code_sh, form.pays, user?.email);
      if (session && user?.email) consumeCredit(user.email);
      setBlurred(false);
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); setResult({ error: true }); }
    setLoading(false);
  };

  const handleEmailVerified = () => { setShowGate(false); setBlurred(false); };
  const handleNavigate = (pg, formData, historyItem) => {
    setPage(pg);
    if (formData) setForm(formData);
    if (historyItem?.result) { setResult(historyItem.result); setBlurred(false); }
    if (historyItem?.form) setForm(historyItem.form);
    window.scrollTo(0, 0);
  };
  const reset = () => { setResult(null); setBlurred(false); setForm({ description:'', pays:'', codeActivite:'', prix:'', devise:'USD', incoterm:'CIF' }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const paysNom = COUNTRIES.find(c => c.code === form.pays)?.[ar ? 'ar' : 'name'] || form.pays;
  const dlPDF = () => generateChecklistPDF({ ...result, description: form.description, pays: form.pays, paysNom, codeActivite: form.codeActivite, date, lang });
  const dlXLS = () => generatePPIExcel({ entreprise: {}, lignes: [{ position: result.classification.code_sh?.substring(0,7), sousPosition: result.classification.code_sh, designation: form.description }], date }).catch(console.error);
  const cpEmail = () => { navigator.clipboard.writeText(emailLang === 'fr' ? result.emails.fr : result.emails.en); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const R = result; const cls = R?.classification; const calc = R?.calcul;

  return (
    <div className="app" dir={ar ? 'rtl' : 'ltr'}>
      {/* ═══ NAVBAR — Glassmorphism ═══ */}
      <nav className="navbar">
        <button className="menu-toggle" onClick={() => setMobileMenu(!mobileMenu)}>☰</button>
        <div className="nav-brand" onClick={() => { setPage('home'); setResult(null); window.scrollTo(0,0); }} style={{ cursor:'pointer' }}>
          <div className="logo">🇩🇿</div>
          <h1>TidjarAI</h1>
        </div>
        <div className={`nav-tabs${mobileMenu ? ' open' : ''}`}>
          {[
            { id:'home', fr:'Accueil', ar:'الرئيسية' },
            { id:'analyse', fr:'Import', ar:'استيراد' },
            { id:'export', fr:'Export', ar:'تصدير' },
            { id:'ppi', fr:'PPI', ar:'PPI' },
            { id:'verify', fr:'Analyse doc.', ar:'تحليل الوثائق' },
            { id:'devis', fr:'Devis', ar:'عروض أسعار' },
            { id:'history', fr:'🕐 Historique', ar:'🕐 السجل' },
          ].map(t => <button key={t.id} className={`nav-tab ${page === t.id ? 'active' : ''}`} onClick={() => { setPage(t.id); setMobileMenu(false); if(t.id==='home') setResult(null); }}>{ar ? t.ar : t.fr}</button>)}
        </div>
        <div className="nav-actions">
          {session && user ? (<>
            <button className="nav-btn" onClick={() => setPage('dashboard')}>👤 {ar ? 'مساحتي' : 'Mon espace'}</button>
            <button className="nav-btn" onClick={() => { signOut().catch(()=>{}); sessionStorage.clear(); window.location.replace('/'); }}>{ar ? 'خروج' : 'Déconnexion'}</button>
          </>) : (
            <button className="nav-btn-primary" onClick={() => setShowGate(true)}>{ar ? 'تسجيل الدخول' : 'Se connecter'}</button>
          )}
          <button className="nav-btn desktop-only" onClick={() => { setPage('history'); }}>🕐</button>
          <button className="nav-btn" onClick={() => { const nl = ar ? 'fr' : 'ar'; setLang(nl); localStorage.setItem('tidjarai_lang', nl); }}>{ar ? 'FR' : 'عربي'}</button>
        </div>
      </nav>

      {/* ═══ HERO — Dynamic per page ═══ */}
      <div className="hero">
        <div className="hero-content">
          {page === 'home' && (<>
            <p style={{ fontSize:'1.8rem', fontWeight:900, marginBottom:'0.2rem', background:'linear-gradient(135deg, #F8FAFC, #7C3AED)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>TidjarAI</p>
            <p style={{ fontSize:'0.82rem', color:'var(--muted)', marginBottom:'0.8rem', direction:'ltr', textAlign:'center' }}>
              <span style={{color:'#7C3AED',fontWeight:700}}>T</span>rade · <span style={{color:'#7C3AED',fontWeight:700}}>I</span>mport/Export · <span style={{color:'#7C3AED',fontWeight:700}}>DJ</span>azaïr · <span style={{color:'#7C3AED',fontWeight:700}}>A</span>utomated · <span style={{color:'#7C3AED',fontWeight:700}}>R</span>egulatory · <span style={{color:'#7C3AED',fontWeight:700}}>AI</span>
            </p>
            <h2>{ar ? 'استيراد. تصدير. ببساطة.' : 'Import. Export. En toute simplicité.'}</h2>
            <p className="tagline">{ar ? 'TidjarAI مساعدك الذكي لكل إجراءاتك الجمركية والتنظيمية في الجزائر — رموز SH، تحليل الوثائق، عروض أسعار وكلاء الجمارك، المطابقة — بنقرات قليلة.' : 'TidjarAI est votre assistant IA pour toutes vos démarches douanières et réglementaires en Algérie — codes SH, analyse de documents, devis transitaires, conformité — en quelques clics.'}</p>
            <div className="hero-ctas">
              <button className="btn-hero btn-hero-primary" onClick={() => setPage('analyse')}>{ar ? '🔍 بدء التحليل مجاناً' : '🔍 Commencer gratuitement'}</button>
            </div>
          </>)}
          {page === 'analyse' && !R && (<>
            <div className="hero-flag">🇩🇿</div>
            <div className="hook">{ar ? '🚀 أداة الاستيراد الذكية للجزائر' : '🚀 L\'outil intelligent d\'import pour l\'Algérie'}</div>
            <h2>{ar ? 'حلل عملية الاستيراد في ثوانٍ' : 'Analysez votre importation en secondes'}</h2>
            <p className="tagline">{ar ? 'حدد الرمز الجمركي، احسب الرسوم والضرائب، واحصل على قائمة الوثائق المطلوبة.' : 'Identifiez le code SH, calculez les droits et taxes, obtenez la checklist documentaire complète.'}</p>
            <div className="hero-ctas">
              <button className="btn-hero btn-hero-primary" onClick={() => document.querySelector('.main')?.scrollIntoView({behavior:'smooth'})}>
                {ar ? '🔍 بدء التحليل مجاناً' : '🔍 Commencer gratuitement'}
              </button>
              <a href="/transitaires" className="btn-hero btn-hero-secondary">{ar ? '🏢 وكلاء الجمارك' : '🏢 Transitaires'}</a>
            </div>
          </>)}
          {page === 'export' && (<>
            <div className="hero-flag">🚢</div>
            <div className="hook">{ar ? '🚀 صدّر منتجاتك الجزائرية إلى العالم' : '🚀 Exportez vos produits algériens vers le monde'}</div>
            <h2>{ar ? 'تحليل عملية التصدير' : 'Analysez votre exportation'}</h2>
            <p className="tagline">{ar ? 'اكتشف الوثائق المطلوبة، الاتفاقيات التفضيلية، والمساعدات المتاحة للتصدير من الجزائر.' : 'Découvrez les documents requis, accords préférentiels et aides disponibles pour exporter depuis l\'Algérie.'}</p>
          </>)}
          {page === 'ppi' && (<>
            <div className="hero-flag">📊</div>
            <div className="hook">{ar ? '📋 الصيغة الرسمية لوزارة التجارة' : '📋 Format officiel du Ministère du Commerce'}</div>
            <h2>{ar ? 'البرنامج التقديري للاستيراد' : 'Programme Prévisionnel d\'Importation'}</h2>
            <p className="tagline">{ar ? 'أنشئ برنامجك التقديري بالصيغة الرسمية mcepe.gov.dz وحمّله بصيغة Excel.' : 'Créez votre PPI au format officiel mcepe.gov.dz et téléchargez-le en Excel.'}</p>
          </>)}
          {page === 'verify' && (<>
            <div className="hero-flag">🔍</div>
            <div className="hook">{ar ? '🤖 تحقق بالذكاء الاصطناعي قبل إرسال الوثائق' : '🤖 Vérifiez par IA avant d\'envoyer vos documents'}</div>
            <h2>{ar ? 'تحليل وثائق الاستيراد' : 'Analyse documents import'}</h2>
            <p className="tagline">{ar ? 'ارفع وثائقك وتحقق من مطابقتها نقطة بنقطة قبل إرسالها للبنك أو الجمارك.' : 'Uploadez vos documents et vérifiez leur conformité point par point avant de les envoyer à la banque ou aux douanes.'}</p>
          </>)}
          {page === 'devis' && (<>
            <div className="hero-flag">📋</div>
            <div className="hook">{ar ? '🏢 وكلاء جمارك متخصصون في منتجك' : '🏢 Transitaires spécialisés dans votre produit'}</div>
            <h2>{ar ? 'طلب عروض أسعار مجانية' : 'Demandez des devis gratuits'}</h2>
            <p className="tagline">{ar ? 'احصل على عروض أسعار من وكلاء جمارك متخصصين في منتجك وميناءك.' : 'Recevez des devis de transitaires spécialisés dans votre produit et votre port d\'arrivée.'}</p>
          </>)}
          {page === 'dashboard' && (<>
            <div className="hero-flag">👤</div>
            <div className="hook">{ar ? '📊 كل عملياتك في مكان واحد' : '📊 Toutes vos opérations en un seul endroit'}</div>
            <h2>{ar ? 'لوحة التحكم' : 'Mon espace'}</h2>
            <p className="tagline">{ar ? 'تحليلاتك، طلباتك، ووثائقك — كل شيء هنا.' : 'Vos analyses, demandes de devis et documents — tout est ici.'}</p>
          </>)}
          {page === 'analyse' && R && (<>
            <div className="hero-flag">✅</div>
            <h2>{ar ? 'نتائج التحليل' : 'Résultats de l\'analyse'}</h2>
          </>)}
        </div>
      </div>

      {/* ═══ BENTO FEATURES ═══ */}
      {!R && page === 'analyse' && (<>
        <div className="bento">
          <div className="bento-grid">
            <div className="bento-card reveal"><div className="bc-icon">🤖</div><h3>{T('feat1_title')}</h3><p>{T('feat1_desc')}</p></div>
            <div className="bento-card reveal"><div className="bc-icon">💰</div><h3>{T('feat2_title')}</h3><p>{T('feat2_desc')}</p></div>
            <div className="bento-card reveal"><div className="bc-icon">📋</div><h3>{T('feat3_title')}</h3><p>{T('feat3_desc')}</p></div>
            <div className="bento-card reveal"><div className="bc-icon">🌍</div><h3>{T('feat4_title')}</h3><p>{T('feat4_desc')}</p></div>
          </div>
        </div>
        <div className="tip-bar"><div className="tip-inner">💡 {T('app_tip')}</div></div>
      </>)}

      {/* HISTORY */}
      <main className="main">
        {/* HISTORY PAGE */}
        {page === 'history' && (
          <div className="card">
            <div className="card-title">🕐 {ar ? 'سجل التحليلات' : 'Historique des analyses'}</div>
            {history.length === 0 ? (
              <div style={{ textAlign:'center', padding:'2rem', color:'var(--muted)' }}>
                <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>📭</div>
                <p>{ar ? 'لا توجد تحليلات بعد.' : 'Aucune analyse encore.'}</p>
                <button className="btn btn-primary" style={{ width:'auto', marginTop:'0.8rem' }} onClick={() => setPage('analyse')}>
                  {ar ? '🔍 إجراء أول تحليل' : '🔍 Faire ma première analyse'}
                </button>
              </div>
            ) : (
              <div>{history.map((h, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.8rem 0', borderBottom:'1px solid var(--border)', cursor:'pointer', flexDirection: ar ? 'row-reverse' : 'row' }}
                  onClick={() => {
                    const allowed = canAnalyze(session);
                    if (allowed !== true) { setShowGate(true); return; }
                    setForm(h.form);
                    if (h.result) { setResult(h.result); setBlurred(false); setPage('analyse'); }
                    else { setResult(null); setPage('analyse'); }
                  }}>
                  <div style={{ flex:1, textAlign: ar ? 'right' : 'left' }}>
                    <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.2rem' }}>{h.description}</div>
                    <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap', justifyContent: ar ? 'flex-end' : 'flex-start' }}>
                      <span className="badge badge-blue">{h.code_sh}</span>
                      {h.result && <span className="badge badge-green">{ar ? 'نتائج محفوظة' : 'Résultats sauvés'}</span>}
                      <span style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{new Date(h.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <span style={{ fontSize:'1.2rem', opacity:0.4 }}>{ar ? '←' : '→'}</span>
                </div>
              ))}</div>
            )}
          </div>
        )}

        {page === 'home' && <LandingHome lang={lang} onNavigate={(p) => { setPage(p); window.scrollTo(0,0); }} onLogin={() => setShowGate(true)} />}
        {page === 'ppi' && <PPIBuilder lang={lang} />}
        {page === 'export' && <ExportModule lang={lang} session={session} onNeedLogin={() => setShowGate(true)} onPaywall={() => setShowPaywall(true)} />}
        {page === 'verify' && <VerifyModule lang={lang} />}
        {page === 'devis' && <QuoteRequestForm lang={lang} simulationData={result?.classification ? { description: form.description, code_sh: result.classification.code_sh, pays: form.pays, prix: form.prix, incoterm: form.incoterm } : null} />}
        {page === 'dashboard' && session && <UserDashboard lang={lang} user={user} onNavigate={handleNavigate} />}
        {page === 'analyse' && (<>
          {/* FORM */}
          <div className="card">
            <div className="card-title">📋 {T('form_title')}</div>
            <form onSubmit={submit}>
              <div className="form-group"><label>{T('form_description')} *</label><input type="text" placeholder={T('form_description_ph')} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></div>
              <div className="form-row">
                <div className="form-group"><label>{T('form_country')} *</label><select value={form.pays} onChange={e => setForm({ ...form, pays: e.target.value })} required><option value="">{T('form_country_ph')}</option>{COUNTRIES.map(c => <option key={c.code} value={c.code}>{ar ? c.ar : c.name} ({c.code})</option>)}</select></div>
                <div className="form-group"><label>{T('form_activity')} *</label><select value={form.codeActivite} onChange={e => setForm({ ...form, codeActivite: e.target.value })} required><option value="">{T('form_activity_ph')}</option>{CODES.map(c => <option key={c.value} value={c.value}>{T(c.key)}</option>)}</select></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1.2fr', gap:'0.75rem' }}>
                <div className="form-group"><label>{T('form_price')}</label><input type="number" placeholder={T('form_price_ph')} value={form.prix} onChange={e => setForm({ ...form, prix: e.target.value })} min="0" step="0.01" /></div>
                <div className="form-group"><label>{T('form_currency')}</label><select value={form.devise} onChange={e => { setForm({ ...form, devise: e.target.value }); setTauxChange(''); }}>{DEVISES.map(d => <option key={d}>{d}</option>)}</select></div>
                <div className="form-group"><label>{T('form_incoterm')}</label><select value={form.incoterm} onChange={e => setForm({ ...form, incoterm: e.target.value })}>{INCOTERMS.map(i => <option key={i.code} value={i.code}>{i.label}</option>)}</select></div>
                <div className="form-group"><label>{ar ? 'سعر الصرف (د.ج)' : 'Taux de change (DZD)'}</label><input type="number" placeholder={`${DEFAULT_TC[form.devise] || 135}`} value={tauxChange} onChange={e => setTauxChange(e.target.value)} min="0" step="0.01" /></div>
              </div>
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <><span className="spinner"/> {T('btn_analyzing')}</> : `🔍 ${T('btn_analyze')}`}</button>
                {R && <button type="button" className="btn btn-reset" onClick={reset}>↺ {T('btn_reset')}</button>}
              </div>
            </form>
          </div>

          {R?.error && <div className="alert alert-danger">⚠️ {T('error_not_found')}</div>}

          {cls && !R.error && (
            <BlurredOverlay blurred={blurred} lang={lang}>
            <div ref={ref} className="results-fade-in">
              {/* Classification */}
              <div className="card">
                <div className="card-title">🏷️ {T('section_classification')}</div>
                <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', marginBottom:'0.5rem' }}>
                  <span className="badge badge-blue">{cls.code_sh}</span>
                  <span className="badge badge-green">{T('confiance')} {cls.confiance}%</span>
                  <span className="badge" style={{ background: cls.source === 'llm' ? '#eef2ff' : '#fffbeb', color: cls.source === 'llm' ? '#3730a3' : '#92400e' }}>{cls.source === 'llm' ? '🤖 IA' : '📋 Local'}</span>
                  {cls.tarif_source && <span className="badge badge-green">📊 {cls.tarif_source}</span>}
                  {cls.daps && <span className="badge badge-red">DAPS</span>}
                </div>
                <p style={{ fontSize:'0.85rem', marginBottom:'0.3rem' }}><strong>{T('description')} :</strong> {ar ? (cls.description_ar || cls.description) : cls.description}</p>
                {cls.explication && <div className="alert alert-info" style={{ fontSize:'0.78rem' }}><span>💡</span><div style={{ textAlign: ar ? 'right' : 'left' }}><strong>{ar ? 'سبب التصنيف:' : 'Raison du classement :'}</strong><br/>{ar ? (cls.explication_ar || cls.explication) : cls.explication}</div></div>}
                {cls.note_precision && <div className="alert alert-warning" style={{ fontSize:'0.76rem' }}><span>📝</span><div>{cls.note_precision}</div></div>}
                {cls.alternatives?.length > 0 && (
                  <div style={{ marginTop:'0.5rem' }}>
                    <div style={{ fontSize:'0.76rem', fontWeight:700, color:'var(--primary)', marginBottom:'0.3rem' }}>
                      {ar ? '📌 رموز بديلة محتملة (حتى 10):' : '📌 Codes alternatifs possibles (jusqu\'à 10) :'}
                      <span style={{ fontWeight:400, color:'var(--muted)', fontSize:'0.7rem', marginInlineStart:'0.3rem' }}>
                        {ar ? '— صف منتجك بدقة أكبر لتصنيف أدق' : '— Décrivez votre produit plus précisément pour un classement plus fin'}
                      </span>
                    </div>
                    {cls.alternatives.map((alt, i) => (
                      <div key={i} style={{ display:'flex', gap:'0.5rem', alignItems:'center', padding:'0.3rem 0', borderBottom:'1px solid #f3f4f6', fontSize:'0.76rem', flexDirection: ar ? 'row-reverse' : 'row' }}>
                        <span className="badge badge-blue" style={{ flexShrink:0 }}>{alt.code_sh}</span>
                        {alt.confiance && <span className="badge" style={{ flexShrink:0, background: alt.confiance >= 70 ? '#ecfdf5' : '#fffbeb', color: alt.confiance >= 70 ? '#065f46' : '#92400e' }}>{alt.confiance}%</span>}
                        <div style={{ textAlign: ar ? 'right' : 'left', flex:1 }}><span style={{ fontWeight:500 }}>{alt.description}</span>{alt.raison && <span style={{ color:'var(--muted)', fontSize:'0.7rem' }}> — {alt.raison}</span>}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tax-grid">
                  <div className="tax-item"><div className="label">{T('dd')}</div><div className="value">{calc ? calc.taux_DD_applique : cls.taux_DD}%</div></div>
                  <div className="tax-item"><div className="label">{T('tva')}</div><div className="value">{cls.taux_TVA}%</div></div>
                  <div className="tax-item"><div className="label">{T('tcs')}</div><div className="value">2%</div></div>
                  {cls.daps && <div className="tax-item tax-item-danger"><div className="label">{T('daps')}</div><div className="value" style={{ color:'#dc2626' }}>{cls.daps_taux}%</div></div>}
                </div>
                {R.accord && <div className={`alert ${R.accord.eligible === true ? 'alert-success' : R.accord.eligible === 'partiel' ? 'alert-warning' : 'alert-info'}`}><span>📜</span><div><strong>{T('accord')} : {R.accord.type}</strong> — {R.accord.document}<br/><span style={{ fontSize:'0.75rem' }}>{R.accord.note}</span></div></div>}
                {cls.daps && <div className="alert alert-danger"><span>🛡️</span><div>{C.daps_warning} ({cls.daps_taux}%)</div></div>}
                <div className="alert alert-warning" style={{ fontSize:'0.72rem' }}><span>⚠️</span><div>{C.disclaimer_sh}</div></div>
              </div>

              {/* CTA Devis — compact */}
              <div style={{ background:'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(16,185,129,0.05))', border:'1px solid rgba(124,58,237,0.15)', borderRadius:12, padding:'0.8rem 1rem', marginBottom:'1rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem' }}>
                <div>
                  <span style={{ fontWeight:600, fontSize:'0.88rem' }}>🏢 {ar ? 'هل تحتاج وكيل جمارك؟' : 'Besoin d\'un transitaire ?'}</span>
                  <span style={{ fontSize:'0.78rem', color:'var(--muted)', marginInlineStart:'0.5rem' }}>{ar ? 'احصل على عروض أسعار مجانية' : 'Devis gratuits de transitaires spécialisés'}</span>
                </div>
                <button className="btn btn-primary" style={{ width:'auto', padding:'0.5rem 1.2rem', fontSize:'0.82rem' }} onClick={() => { setPage('devis'); window.scrollTo(0,0); }}>📋 {ar ? 'طلب عرض' : 'Demander un devis'}</button>
              </div>

              {/* Documents */}
              <div className="card">
                <div className="card-title">📄 {T('section_documents')}</div>
                {(() => {
                  const allDocs = [...R.documents.fournisseur, ...R.documents.importateur];
                  const order = ar
                    ? ['قبل الطلب', 'قبل الشحن', 'بعد الشحن', 'قبل التوطين', 'لملف الجمارك']
                    : ['AVANT commande', 'Avant expédition', 'Après expédition', 'Avant domiciliation', 'Pour dossier'];
                  const labels = ar
                    ? { 'قبل الطلب':'1️⃣ قبل تقديم الطلب', 'قبل الشحن':'2️⃣ قبل الشحن (يطلب من المورد)', 'بعد الشحن':'3️⃣ بعد الشحن', 'قبل التوطين':'4️⃣ قبل التوطين البنكي (جانب المستورد)', 'لملف الجمارك':'5️⃣ لملف الجمارك' }
                    : { 'AVANT commande':'1️⃣ Avant de passer commande', 'Avant expédition':'2️⃣ Avant expédition (à demander au fournisseur)', 'Après expédition':'3️⃣ Après expédition', 'Avant domiciliation':'4️⃣ Avant domiciliation bancaire (côté importateur)', 'Pour dossier':'5️⃣ Pour le dossier douanier' };
                  const groups = {};
                  allDocs.forEach(d => {
                    const t = d.timing || (ar ? 'قبل الشحن' : 'Avant expédition');
                    const key = order.find(o => t.includes(o)) || order[1];
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(d);
                  });
                  return order.filter(k => groups[k]?.length).map(k => (
                    <div key={k} style={{ marginBottom:'0.8rem' }}>
                      <div className="doc-phase">{labels[k] || k}</div>
                      <ul className="doc-list">{groups[k].map(d => (
                        <li key={d.id} className="doc-item"><span className="doc-icon">{d.obligatoire ? '✅' : '⭐'}</span><div>
                          <strong>{d.nom}</strong>
                          {d.recommande && <span className="badge badge-yellow" style={{ marginInlineStart:'0.3rem' }}>{T('recommande')}</span>}
                          <div className="doc-details">{d.details}</div>
                          {d.mentions && <div className="doc-mentions"><div className="doc-mentions-title">{ar ? '📋 البيانات الإلزامية:' : '📋 Mentions obligatoires :'}</div><ul>{(Array.isArray(d.mentions) ? d.mentions : []).map((m,i) => <li key={i}>{m}</li>)}</ul></div>}
                          {d.howto && <div className="doc-howto">→ {d.howto}</div>}
                          {d.warning && <div className="doc-warning">⚠️ {d.warning}</div>}
                        </div></li>
                      ))}</ul>
                    </div>
                  ));
                })()}
              </div>

              {/* Étiquetage */}
              <div className="card">
                <div className="card-title">🏷️ {T('section_etiquetage')}</div>
                {R.etiquetage.map((r, i) => (
                  <div key={i} style={{ marginBottom:'0.5rem' }}>
                    <div style={{ fontWeight:600, fontSize:'0.8rem' }}>{r.obligatoire ? '✅' : '📌'} {r.titre} {r.base_legale && <span style={{ fontSize:'0.65rem', color:'var(--muted)' }}>({r.base_legale})</span>}</div>
                    {r.description && <p style={{ fontSize:'0.75rem', color:'var(--muted)' }}>{r.description}</p>}
                    {r.mentions && <ul style={{ fontSize:'0.72rem', color:'var(--muted)', paddingInlineStart:'1.2rem', marginTop:'0.15rem' }}>{r.mentions.map((m,j) => <li key={j}>{m}</li>)}</ul>}
                  </div>
                ))}
              </div>

              {/* Calcul */}
              {calc && (
                <div className="card">
                  <div className="card-title">💰 {T('section_calcul')}</div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>
                    <tbody>
                      {[[T('valeur_cif'), calc.valeur_cif_dzd], [`${T('dd')} (${calc.taux_DD_applique}%)`, calc.DD], [`${T('tcs')} (2%)`, calc.TCS],
                        ...(calc.DAPS ? [[`${T('daps')} (${calc.daps_taux}%)`, calc.DAPS]] : []), [T('tva'), calc.TVA],
                      ].map(([l, v], i) => <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}><td style={{ padding:'0.4rem 0' }}>{l}</td><td>{FMT(v)} DZD</td></tr>)}
                      <tr style={{ borderTop:'2px solid var(--primary)' }}><td style={{ padding:'0.5rem 0', fontWeight:700 }}>{T('total_taxes')}</td><td style={{ fontWeight:700, color:'var(--accent)' }}>{FMT(calc.total_taxes)} DZD</td></tr>
                      <tr style={{ background:'#f9fafb' }}><td style={{ padding:'0.5rem 0', fontWeight:800 }}>{T('cout_total')}</td><td style={{ fontWeight:900, fontSize:'1.05rem', color:'var(--primary)' }}>{FMT(calc.cout_total)} DZD</td></tr>
                    </tbody>
                  </table>
                  {calc.tdb && <div className="alert alert-info" style={{ marginTop:'0.5rem' }}><span>🏦</span><div><strong>{calc.tdb.label}</strong> — {FMT(calc.tdb.montant)} DZD</div></div>}
                  {calc.comparaison && (
                    <div className="comparateur">
                      <div className="comparateur-title">📊 {T('comparateur_title')}</div>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                        <thead><tr style={{ borderBottom:'2px solid var(--primary)' }}><th></th><th style={{ color:'var(--emerald)' }}>{T('avec_accord')}</th><th style={{ color:'#dc2626' }}>{T('sans_accord')}</th></tr></thead>
                        <tbody>
                          <tr style={{ borderBottom:'1px solid #d1fae5' }}><td>{T('dd')}</td><td>{FMT(calc.DD)}</td><td>{FMT(calc.comparaison.DD)}</td></tr>
                          <tr style={{ borderBottom:'1px solid #d1fae5' }}><td>{T('total_taxes')}</td><td>{FMT(calc.total_taxes)}</td><td>{FMT(calc.comparaison.total_taxes)}</td></tr>
                          <tr style={{ background:'#d1fae5' }}><td colSpan={2} style={{ fontWeight:700, color:'var(--emerald)' }}>💰 {T('economie_accord')}</td><td style={{ fontWeight:700, color:'var(--emerald)' }}>{FMT(calc.comparaison.economie)} DZD</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ fontSize:'0.7rem', color:'var(--muted)', marginTop:'0.3rem' }}>{T('taux_change')} : 1 {calc.devise} = {calc.taux_change} DZD</div>
                  <div className="alert alert-warning" style={{ fontSize:'0.7rem', marginTop:'0.4rem' }}><span>⚠️</span><div>{C.disclaimer_calcul}</div></div>
                </div>
              )}

              {/* Economies */}
              {R.economies.length > 0 && (
                <div className="card">
                  <div className="card-title">💡 {T('section_economies')}</div>
                  {R.economies.map((e, i) => <div key={i} className="alert alert-success"><span>💰</span><div><strong>{e.message}</strong><br/><span style={{ fontSize:'0.75rem' }}>{e.action}</span></div></div>)}
                </div>
              )}

              {/* Email — ALWAYS LTR */}
              <div className="card email-section">
                <div className="card-title" style={{ direction:'ltr', textAlign:'left', flexDirection:'row' }}>✉️ {ar ? 'بريد المورد' : T('section_email')}</div>
                <div className="email-tabs">
                  <button className={`email-tab ${emailLang === 'fr' ? 'active' : ''}`} onClick={() => setEmailLang('fr')}>🇫🇷 FR</button>
                  <button className={`email-tab ${emailLang === 'en' ? 'active' : ''}`} onClick={() => setEmailLang('en')}>🇬🇧 EN</button>
                </div>
                <div className="email-content">{emailLang === 'fr' ? R.emails.fr : R.emails.en}</div>
                <button className="btn btn-outline" style={{ marginTop:'0.5rem' }} onClick={cpEmail}>{copied ? '✅ Copié' : '📋 Copier'}</button>
              </div>

              {/* Downloads */}
              <div className="card">
                <div className="card-title">📥 {T('section_downloads')}</div>
                <div className="downloads">
                  <button className="btn btn-outline" onClick={dlPDF}>📄 {T('download_pdf')}</button>
                  <button className="btn btn-outline" onClick={dlXLS}>📊 {T('download_excel')}</button>
                  <button className="btn btn-outline" onClick={cpEmail}>{copied ? '✅' : '✉️'} {T('download_email')}</button>
                </div>
              </div>

              {/* CTA Devis */}
              <div className="card" style={{ background:'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(16,185,129,0.05))', border:'1px solid rgba(124,58,237,0.15)' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>🏢</div>
                  <h3 style={{ fontSize: '1rem', color: 'var(--text)', marginBottom: '0.3rem' }}>
                    {ar ? 'هل تحتاج إلى وكيل جمارك؟' : 'Besoin d\'un transitaire ?'}
                  </h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.8rem' }}>
                    {ar ? 'احصل على عروض أسعار مجانية من وكلاء جمارك متخصصين في منتجك.' : 'Recevez gratuitement des devis de transitaires spécialisés dans votre produit.'}
                  </p>
                  <button className="btn btn-primary" style={{ width: 'auto', padding: '0.7rem 2rem', fontSize: '0.9rem' }} onClick={() => { setPage('devis'); window.scrollTo(0, 0); }}>
                    📋 {ar ? 'طلب عروض أسعار مجانية' : 'Demander des devis gratuits'}
                  </button>
                </div>
              </div>

              <div className="disclaimer">
                <strong>⚠️ {T('disclaimer_title')} — {date}</strong><br/><br/>
                {C.disclaimer_general}<br/><br/>
                {(R.accord?.type?.includes('UE') || R.accord?.type?.includes('الأوروبي')) && <>{C.disclaimer_ue}<br/><br/></>}
                {C.disclaimer_docs}
              </div>
            </div>
            </BlurredOverlay>
          )}
          {showGate && <EmailGateModal lang={lang} onClose={() => setShowGate(false)} />}
        </>)}
      </main>

      {/* Global gate modal (works from any page) */}

      {/* Global modal — accessible from navbar "Se connecter" */}
      {showGate && !session && <EmailGateModal lang={lang} onClose={() => setShowGate(false)} />}
      {showPaywall && (
        <div onClick={() => setShowPaywall(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#141927', borderRadius:20, padding:'2rem', maxWidth:420, width:'90%', textAlign:'center', border:'1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => setShowPaywall(false)} style={{ position:'absolute', top:12, right:14, background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:'#94A3B8' }}>✕</button>
            <div style={{ fontSize:'2.5rem', marginBottom:'0.5rem' }}>🔒</div>
            <h2 style={{ fontSize:'1.1rem', color:'#F8FAFC', marginBottom:'0.5rem' }}>{ar ? 'لقد وصلت إلى الحد الأقصى' : 'Limite d\'analyses atteinte'}</h2>
            <p style={{ fontSize:'0.85rem', color:'#94A3B8', lineHeight:1.7, marginBottom:'1rem' }}>
              {ar ? 'لقد استخدمت جميع تحليلاتك المجانية هذا الشهر. اتصل بنا للحصول على رصيد إضافي.' : 'Vous avez utilisé toutes vos analyses gratuites ce mois-ci. Contactez-nous pour obtenir des crédits supplémentaires.'}
            </p>
            <a href="mailto:contact@tidjarai.com" className="btn btn-primary" style={{ width:'auto', padding:'0.7rem 1.5rem', textDecoration:'none', display:'inline-block' }}>
              📧 contact@tidjarai.com
            </a>
            <p style={{ fontSize:'0.72rem', color:'#64748b', marginTop:'0.8rem' }}>{ar ? 'سنرد عليك في أقرب وقت.' : 'Nous vous répondrons rapidement.'}</p>
          </div>
        </div>
      )}

      <footer className="footer">
        <div style={{ marginBottom:'0.8rem' }}>
          <a href="/transitaires">🏢 {ar ? 'وكلاء الجمارك' : 'Transitaires'}</a>
          <a href="/devis" onClick={e => { e.preventDefault(); setPage('devis'); window.scrollTo(0,0); }}>📋 {ar ? 'طلب عرض أسعار' : 'Demander un devis'}</a>
          <a href="/transitaire/dashboard">🏢 {ar ? 'فضاء وكيل الجمارك' : 'Espace transitaire'}</a>
        </div>
        <div>TidjarAI 🇩🇿 — © 2026</div>
        <div style={{ marginTop:'0.3rem' }}>{T('footer')}</div>
      </footer>
    </div>
  );
}


function getLang() { return localStorage.getItem('tidjarai_lang') || 'fr'; }

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/transitaire/inscription" element={<ForwarderRegistration lang={getLang()} />} />
        <Route path="/transitaire/dashboard" element={<ForwarderDashboard />} />
        <Route path="/transitaire/wallet" element={<ForwarderWallet lang={getLang()} />} />
        <Route path="/transitaires" element={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0A0F1E',color:'#94A3B8'}}><div style={{textAlign:'center'}}><h2>🏢 Bientôt disponible</h2><a href="/" style={{color:'#7C3AED'}}>← Retour</a></div></div>} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}
